import { FC, useState, useRef, useEffect, ChangeEvent, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InvoiceItem } from '@libs/schemas';
import { Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { MoneyFromCents } from '@libs/services/money';
import { Textarea } from '@/components/ui/textarea';
import { trpc, Product, AddOn } from 'src/trpc';
import { cn } from '@/lib/utils';

interface InvoiceItemEditorProps {
  item: InvoiceItem;
  index: number;
  onUpdate: (index: number, changes: Record<string, number | string | boolean | undefined>) => void;
  onRemove: (index: number) => void;
  showValidationError?: boolean;
}

type SelectableItem = { type: 'product'; data: Product } | { type: 'addon'; data: AddOn };

export const InvoiceItemEditor: FC<InvoiceItemEditorProps> = ({
  item,
  index,
  onUpdate,
  onRemove,
  showValidationError = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch all products (up to 1000)
  const { data: productsData, isLoading: loadingProducts } =
    trpc.admin.product.getAllProducts.useQuery({
      offset: 0,
      limit: 1000,
    });

  // Fetch all add-ons (up to 1000)
  const { data: addOnsData, isLoading: loadingAddOns } = trpc.admin.addOn.getAll.useQuery({
    offset: 0,
    limit: 1000,
  });

  const isLoading = loadingProducts || loadingAddOns;

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    const products = productsData?.products || [];
    if (!searchTerm.trim()) return products.filter((p) => !p.archived);
    const lowerSearch = searchTerm.toLowerCase();
    return products.filter(
      (p) =>
        !p.archived &&
        (p.label.toLowerCase().includes(lowerSearch) ||
          p.description?.toLowerCase().includes(lowerSearch)),
    );
  }, [productsData?.products, searchTerm]);

  // Filter add-ons based on search term
  const filteredAddOns = useMemo(() => {
    const addOns = addOnsData?.addOns || [];
    if (!searchTerm.trim()) return addOns.filter((a) => !a.archived);
    const lowerSearch = searchTerm.toLowerCase();
    return addOns.filter(
      (a) =>
        !a.archived &&
        (a.title.toLowerCase().includes(lowerSearch) ||
          a.description?.toLowerCase().includes(lowerSearch)),
    );
  }, [addOnsData?.addOns, searchTerm]);

  // Create flat list for keyboard navigation (add-ons first, then products)
  const allItems = useMemo((): SelectableItem[] => {
    const items: SelectableItem[] = [];
    filteredAddOns.forEach((addon) => items.push({ type: 'addon', data: addon }));
    filteredProducts.forEach((product) => items.push({ type: 'product', data: product }));
    return items;
  }, [filteredAddOns, filteredProducts]);

  const hasItems = filteredProducts.length > 0 || filteredAddOns.length > 0;

  const handleItemSelect = (selectableItem: SelectableItem | null) => {
    if (!selectableItem) {
      onUpdate(index, {
        label: '',
        description: '',
        productId: undefined,
        unitPriceInCents: 0,
        totalPriceInCents: 0,
        collectTaxes: false,
      });
    } else if (selectableItem.type === 'product') {
      const product = selectableItem.data;
      const totalPriceInCents = item.quantity * product.priceInCents;
      onUpdate(index, {
        label: product.label,
        description: product.description || '',
        productId: product._id,
        unitPriceInCents: product.priceInCents,
        totalPriceInCents: totalPriceInCents,
        collectTaxes: product.collectTaxes || false,
      });
    } else {
      const addon = selectableItem.data;
      const totalPriceInCents = item.quantity * addon.priceInCents;
      onUpdate(index, {
        label: addon.title,
        description: addon.description || '',
        productId: addon._id?.toString(),
        unitPriceInCents: addon.priceInCents,
        totalPriceInCents: totalPriceInCents,
        collectTaxes: false,
      });
    }
    setShowDropdown(false);
    setSelectedIndex(-1);
    setSearchTerm('');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onUpdate(index, { label: value });
    setSearchTerm(value);
    setSelectedIndex(-1);

    // Show dropdown when typing
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  const handleInputFocus = () => {
    // Only auto-show dropdown for empty items (new items)
    if (!item.label || item.label.trim() === '') {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || allItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allItems.length) {
          handleItemSelect(allItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleProductClick = (product: Product) => {
    handleItemSelect({ type: 'product', data: product });
  };

  const handleAddOnClick = (addon: AddOn) => {
    handleItemSelect({ type: 'addon', data: addon });
  };

  // Calculate index offset for products (add-ons come first in allItems)
  const addOnsCount = filteredAddOns.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasEmptyLabel = !item.label || item.label.trim() === '';
  const showError = showValidationError && hasEmptyLabel;

  return (
    <div
      className={cn(
        'border rounded-lg p-4',
        showError ? 'border-red-300 bg-red-50' : 'border-gray-200',
      )}
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <label className={cn('label text-xs', showError && 'text-red-600')} htmlFor={`label`}>
            Product Name: {showError && <span className="text-red-600">*Required</span>}
          </label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                ref={inputRef}
                id={`label`}
                type="text"
                placeholder="Search products or add-ons..."
                value={item.label}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                className={cn(
                  'w-full',
                  showError && 'border-red-300 focus:border-red-500 focus:ring-red-500',
                )}
              />

              {/* Custom Dropdown */}
              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
                >
                  {/* Loading state */}
                  {isLoading ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">Loading...</div>
                  ) : !hasItems ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      No items match your search
                    </div>
                  ) : (
                    <>
                      {/* Add-ons Section */}
                      {filteredAddOns.length > 0 && (
                        <>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-200 uppercase tracking-wide">
                            Add-ons ({filteredAddOns.length})
                          </div>
                          {filteredAddOns.map((addon, idx) => (
                            <div
                              key={addon._id?.toString()}
                              className={cn(
                                'px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0',
                                idx === selectedIndex && 'bg-blue-50 border-blue-200 text-blue-900',
                              )}
                              onClick={() => handleAddOnClick(addon)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{addon.title}</div>
                                  {addon.description && (
                                    <div className="text-xs text-gray-500 truncate">
                                      {addon.description}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right ml-2">
                                  <div className="text-sm font-medium text-gray-900">
                                    ${(addon.priceInCents / 100).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Products Section */}
                      {filteredProducts.length > 0 && (
                        <>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-200 uppercase tracking-wide">
                            Products ({filteredProducts.length})
                          </div>
                          {filteredProducts.map((product, idx) => (
                            <div
                              key={product._id}
                              className={cn(
                                'px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0',
                                addOnsCount + idx === selectedIndex &&
                                  'bg-blue-50 border-blue-200 text-blue-900',
                              )}
                              onClick={() => handleProductClick(product)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{product.label}</div>
                                  {product.description && (
                                    <div className="text-xs text-gray-500 truncate">
                                      {product.description}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right ml-2">
                                  <div className="text-sm font-medium text-gray-900">
                                    ${(product.priceInCents / 100).toFixed(2)}
                                  </div>
                                  {product.collectTaxes && (
                                    <div className="text-xs text-gray-500">+ Tax</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="label text-xs" htmlFor={`product`}>
            Product Description:
          </label>
          <Textarea
            placeholder="Product description"
            value={item.description}
            className="h-8"
            onChange={(e) => onUpdate(index, { description: e.target.value })}
          />
        </div>

        <div className="flex items-stretch gap-3">
          <div className="flex flex-grow flex-col">
            <label className="label text-xs" htmlFor={`quantity-${index}`}>
              Quantity
            </label>
            <Input
              id={`quantity-${index}`}
              inputMode="numeric"
              type="number"
              min="1"
              value={item.quantity}
              onChange={(e) => {
                const quantity = parseInt(e.target.value) || 1;
                const totalPrice = quantity * item.unitPriceInCents;
                onUpdate(index, {
                  quantity: quantity,
                  totalPriceInCents: totalPrice,
                });
              }}
            />
          </div>
          <div className="flex flex-grow flex-col">
            <label className="label text-xs" htmlFor={`unitPrice-${index}`}>
              Unit Price ($)
            </label>
            <Input
              id={`unitPrice-${index}`}
              inputMode="decimal"
              type="number"
              step="0.01"
              value={item.unitPriceInCents ? item.unitPriceInCents / 100 : ''}
              onChange={(e) => {
                const unitPrice = Math.round(parseFloat(e.target.value) * 100) || 0;
                const totalPrice = item.quantity * unitPrice;
                onUpdate(index, {
                  unitPriceInCents: unitPrice,
                  totalPriceInCents: totalPrice,
                });
              }}
            />
          </div>
        </div>

        <div className="flex flex-row justify-end">
          <div className="flex items-center gap-2">
            <label className="label text-xs" htmlFor={`collectTaxes-${index}`}>
              Collect Taxes
            </label>
            <Switch
              id={`collectTaxes-${index}`}
              checked={item.collectTaxes}
              onCheckedChange={(checked) => onUpdate(index, { collectTaxes: Boolean(checked) })}
              className="data-[state=checked]:bg-primary"
            />
          </div>
        </div>

        <div className="flex flex-row justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <span className="font-semibold">
            Total: {MoneyFromCents(item.totalPriceInCents || 0).toFormat()}
          </span>
        </div>
      </div>
    </div>
  );
};
